import { useEffect, useState, useMemo } from 'react';
import { mapRepRowWindow, type FundMetric } from '../lib/fundraisingWindow';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useCockpitStore } from '../store/cockpitStore';
import { useSalesTeamsStore, resolveDefaultTeamId } from '../store/salesTeamsStore';
import {
  AdoptionStrip, ActivityPulse, RepAccountabilityTable,
  PipelineConversions, CustomerHealth, HygienePanel, TeamSwitcher, TeamFundraisingYoY,
} from '../components/cockpit';
import { PendingRequests } from '../components/requests';

export const TeamDashboard = () => {
  const { canViewAllPeople } = useAuthStore();
  const { data, isLoading, error, fetch } = useCockpitStore();
  const { teams, loadAll, loadMine } = useSalesTeamsStore();
  const [teamId, setTeamId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  // CRM-121: the toggle used to reset to YTD on every page load, so a manager who
  // works in the season view had to re-select it each time. Persist the choice.
  const [fundMetric, setFundMetricState] = useState<FundMetric>(() => {
    try {
      const saved = localStorage.getItem('teamFundMetric');
      if (saved === 'calYtd' || saved === 'season') return saved;
    } catch { /* ignore */ }
    return 'calYtd';
  });
  const setFundMetric = (m: FundMetric) => {
    setFundMetricState(m);
    try { localStorage.setItem('teamFundMetric', m); } catch { /* ignore */ }
  };

  // Map each rep row's selected window into the legacy cents/count fields the table renders.
  const displayRepRows = useMemo(
    () => (data?.repRows ?? []).map((r) => mapRepRowWindow(r, fundMetric)),
    [data?.repRows, fundMetric],
  );

  // Load the team lists once; resolve the default selection (my first team) BEFORE
  // marking ready, so the first cockpit fetch uses the right teamId (no double-fetch).
  useEffect(() => {
    if (!canViewAllPeople()) return;
    Promise.all([loadAll(), loadMine()])
      .then(() => {
        setTeamId(resolveDefaultTeamId(useSalesTeamsStore.getState().mine));
        setReady(true);
      })
      .catch(() => setReady(true)); // a transient /mine or /sales-teams failure still loads the cockpit (All Teams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Period picker temporarily hidden (2026-06): pin the Team cockpit to the school year so
  // fundraiser + activity numbers are both school-year-to-date and don't imply day/week/month
  // slicing we can't yet back with data. Restore <PeriodToggle> + period state to re-enable.
  useEffect(() => {
    if (canViewAllPeople() && ready) fetch('team', 'year', teamId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, teamId]);

  if (!canViewAllPeople()) return <Navigate to="/" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Team</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <TeamSwitcher teams={teams} value={teamId} onChange={setTeamId} />
          {/* PeriodToggle temporarily hidden — cockpit pinned to School Year (see fetch effect). */}
        </div>
      </div>

      <PendingRequests variant="strip" />

      {error && <div className="card p-4 text-sm text-rose-600 dark:text-rose-400">{error}</div>}
      {isLoading && !data && <div className="card p-6 text-sm text-gray-400 dark:text-gray-500">Loading…</div>}

      {/* Gate on scope so a stale mine-scope payload (from / in the shared store)
          never flashes under the Team heading before the team-scope refetch lands. */}
      {data && data.scope === 'team' && (
        <>
          {data.teamFundraising && <TeamFundraisingYoY data={data.teamFundraising} metric={fundMetric} onMetricChange={setFundMetric} bounds={data.fundraisingWindows} />}
          {data.adoption && <AdoptionStrip data={data.adoption} />}
          <ActivityPulse data={data.activityPulse} period={data.period} />
          {data.repRows && <RepAccountabilityTable rows={displayRepRows} period={data.period} teamId={data.teamId} metric={fundMetric} unattributed={data.unattributed} season={data.teamFundraising?.season ?? null} />}
          <PipelineConversions pipeline={data.pipeline} temperature={data.temperature} conversions={data.conversions} scope="all" />
          <CustomerHealth data={data.customerHealth} />
          <HygienePanel data={data.hygiene} />
        </>
      )}
    </div>
  );
};
